import { censusLoginPrecheck } from "@/api/census-account-client";
import { addCensusPhone } from "@/api/census-add-phone-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { fetchCensusIdentity } from "@/hooks/use-census-session";
import { track } from "@cakewalk/frontend-api";
import { Eye, EyeOff, Lock, Mail, Phone } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2InlineIconButton,
  V2Input,
  V2InputOTP,
  V2Typography,
} from "@cakewalk/ui/v2";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";
import { OtpResend } from "./OtpResend";

/**
 * Step 1 of the company-census flow: the client logs into the account they
 * already have with us — or starts creating one.
 *
 * Real Cognito auth via Amplify v6 (`signIn` / `confirmSignIn`). Supports a
 * plain sign-in and an inline SMS / TOTP MFA challenge. A user who has a login
 * but hasn't set up MFA yet is offered an inline "add your phone" screen so
 * they can enrol in SMS 2FA without leaving the census (mirrors pulpo/wallet):
 * we set the phone via the platform API, restart sign-in, and drive the SMS
 * challenge. Other Cognito steps (new-password, TOTP-only setup) still surface a
 * friendly message pointing at the main app. The flow is owner/manager-only, so
 * we read the access role from the fresh token (`canManage`) and hand it to the
 * orchestrator.
 */

/** "(212) 555-0100" → "***-***-0100" for the OTP screen's delivery hint. */
function maskPhone(formatted: string): string {
  const digits = formatted.replace(/\D/g, "");
  if (digits.length < 4) return "your phone";
  return `***-***-${digits.slice(-4)}`;
}

/** "5551234567" → "(555) 123-4567" (progressive, mirrors wallet/cockpit). */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const RESEND_COOLDOWN_SECONDS = 60;

export function CompanyCensusLoginStep({
  defaultEmail,
  playBuild = false,
  ceremony = null,
  onLogin,
  onActivate,
  onGetStarted,
  onForgotPassword,
}: {
  defaultEmail?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onLogin: (email: string, canManage: boolean) => void;
  /**
   * Existing customers with no login yet → the activation flow. Carries an
   * optional email to prefill and a notice to explain why we sent them there
   * (e.g. a known owner who tried to log in but never set a password). Still
   * used by the failed-sign-in precheck reroute.
   */
  onActivate: (prefill?: { email?: string; notice?: string }) => void;
  /** "First time?" → the email-first entry that branches login/activate/prospect. */
  onGetStarted: () => void;
  onForgotPassword: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [maskedDestination, setMaskedDestination] = useState<string | undefined>(undefined);
  // SMS codes can be re-sent (and the phone changed when we're enrolling);
  // authenticator-app codes can't. The resend cooldown/countdown lives inside
  // <OtpResend>, isolated so its per-second tick never re-renders this screen —
  // a live countdown and Messages autofill coexist (see OtpResend.tsx).
  const [otpViaSms, setOtpViaSms] = useState(false);

  // MFA-setup sub-state: the login is valid but no MFA is enrolled yet, so we
  // collect a phone and enrol in SMS 2FA inline. `enrollingPhone` marks that the
  // OTP screen was reached via this path, which unlocks "change phone number".
  const [awaitingPhone, setAwaitingPhone] = useState(false);
  const [phone, setPhone] = useState("");
  const [enrollingPhone, setEnrollingPhone] = useState(false);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  // Read the access role straight from the freshly-minted token so the
  // orchestrator can gate the flow without waiting for a page refresh.
  const finishLogin = async () => {
    const identity = await fetchCensusIdentity();
    // tracking-plan: Flow 11 (company_census) > census_login_succeeded (P1)
    track("census_login_succeeded", { flow_name: "company_census", step_name: "login" });
    onLogin(email.trim(), identity.canManage);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const response = await signIn({ username: email.trim(), password });
      if (response.isSignedIn) {
        await finishLogin();
        return;
      }
      const step = response.nextStep?.signInStep;
      if (step === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
        setOtpViaSms(true);
        // tracking-plan: Flow 11 (company_census) > census_mfa_challenged (P2)
        track("census_mfa_challenged", { flow_name: "company_census", step_name: "login" });
        setAwaitingOtp(true);
        return;
      }
      if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        // Authenticator-app codes have no delivery destination — and no resend.
        setMaskedDestination("your authenticator app");
        setOtpViaSms(false);
        // tracking-plan: Flow 11 (company_census) > census_mfa_challenged (P2)
        track("census_mfa_challenged", { flow_name: "company_census", step_name: "login" });
        setAwaitingOtp(true);
        return;
      }
      if (
        step === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION" ||
        step === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION" ||
        step === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"
      ) {
        // Credentials are valid but MFA isn't set up yet. Collect a phone and
        // enrol in SMS 2FA inline (mirrors pulpo/wallet) instead of dead-ending.
        setAwaitingPhone(true);
        return;
      }
      setError(
        `Additional verification required (${step ?? "UNKNOWN_STEP"}). Finish signing in on the main app, then return here.`,
      );
    } catch (err) {
      // A live session from a previous visit makes Amplify reject a fresh
      // sign-in ("There is already a signed in user"). Treat it as
      // already-authenticated and continue into the flow.
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      // A known owner who has never set a password on the new platform has no
      // Cognito login, so sign-in fails here. Rather than a bare "wrong
      // password", check whether they're an owner on file with no login yet and,
      // if so, send them to activation with an explainer. (We can't tell this
      // from the Cognito error alone — a missing user and a wrong password both
      // surface as NotAuthorizedException when user-existence errors are hidden.)
      const name = err instanceof Error ? err.name : "";
      if (name === "NotAuthorizedException" || name === "UserNotFoundException") {
        try {
          const { isKnownOwner, hasLogin } = await censusLoginPrecheck(email.trim());
          if (isKnownOwner && !hasLogin) {
            onActivate({
              email: email.trim(),
              notice:
                "Good news — we found your details in our system. You just haven't set a password on our new platform yet. Let's activate your account.",
            });
            return;
          }
        } catch {
          // Precheck failed (network / rate limit) — fall through to the
          // normal sign-in error below.
        }
      }
      // tracking-plan: Flow 11 (company_census) > census_login_failed (P1)
      track("census_login_failed", {
        flow_name: "company_census",
        step_name: "login",
        failure_reason: err instanceof Error ? err.name : "unknown",
      });
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Restart the sign-in so Cognito sends a fresh SMS code. Used right after
   * the phone is set (first send) and by Resend on the OTP screen. The pool
   * offers SMS + TOTP; the census only enrols SMS, so pick it when Cognito
   * asks. Returns "signed-in" | "sms" | the unexpected step name.
   */
  const restartSmsSignIn = async (): Promise<string> => {
    const response = await signIn({ username: email.trim(), password });
    if (response.isSignedIn) return "signed-in";
    let step = response.nextStep?.signInStep;
    if (
      step === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION" ||
      step === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION"
    ) {
      const selection = await confirmSignIn({ challengeResponse: "SMS" });
      step = selection.nextStep?.signInStep;
    }
    if (step === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
      if (
        response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" &&
        response.nextStep.codeDeliveryDetails?.destination
      ) {
        setMaskedDestination(response.nextStep.codeDeliveryDetails.destination);
      }
      return "sms";
    }
    return step ?? "UNKNOWN_STEP";
  };

  // MFA enrolment: set the phone via the platform API, then restart sign-in and
  // drive the SMS challenge into the shared OTP screen. Called from the
  // add-phone sub-state below (both first time and via "change phone number").
  const handlePhoneSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setError("Enter a 10-digit US mobile number.");
      return;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      await addCensusPhone(email.trim(), phone);

      const outcome = await restartSmsSignIn();
      if (outcome === "signed-in") {
        await finishLogin();
        return;
      }
      if (outcome === "sms") {
        // The code goes to the number the user just entered — mask that.
        setMaskedDestination(maskPhone(phone));
        setEnrollingPhone(true);
        setOtpViaSms(true);
        setAwaitingPhone(false);
        setOtp("");
        // tracking-plan: Flow 11 (company_census) > census_mfa_challenged (P2)
        track("census_mfa_challenged", { flow_name: "company_census", step_name: "login" });
        setAwaitingOtp(true);
        return;
      }
      setError(
        `Additional verification required (${outcome}). Finish signing in on the main app, then return here.`,
      );
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      setError(err instanceof Error ? err.message : "We couldn't set up your phone. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitOtpValue = async (code: string) => {
    if (isLoading) return;
    const cleanCode = code.replace(/\D/g, "");
    if (cleanCode.length === 0) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await confirmSignIn({ challengeResponse: cleanCode });
      if (result.isSignedIn) {
        await finishLogin();
        return;
      }
      setError(`Authentication incomplete (${result.nextStep?.signInStep ?? "UNKNOWN_STEP"}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await submitOtpValue(otp);
  };

  // Re-send the SMS by restarting the sign-in (same pattern as the shared
  // cockpit/wallet login page). <OtpResend> owns the cooldown/in-flight state
  // and only offers this once the countdown reaches zero: resolve to restart the
  // countdown, throw to leave the button armed for a retry.
  const handleResendCode = async () => {
    setError(undefined);
    try {
      const outcome = await restartSmsSignIn();
      if (outcome === "signed-in") {
        await finishLogin();
        return;
      }
      if (outcome !== "sms") {
        setError(`Failed to resend the code (${outcome}). Try again.`);
        throw new Error(`resend-unexpected-step:${outcome}`);
      }
      // tracking-plan: Flow 11 (company_census) > census_mfa_code_resent (P2)
      track("census_mfa_code_resent", { flow_name: "company_census", step_name: "login" });
      setOtp("");
    } catch (err) {
      setError((prev) => prev ?? (err instanceof Error ? err.message : "Failed to resend code"));
      throw err;
    }
  };

  // Key each screen's root so it mounts a FRESH subtree. Without this, React
  // reuses the same <form> DOM node across screens (add-phone → otp), and the
  // browser treats that form's autofill as already handled — suppressing the
  // SMS one-time-code suggestion on the OTP field. Same fix as the activation
  // step (PR #150), which keys its shared shell by phase.
  if (awaitingPhone) {
    return (
      <div key="add-phone" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                One more step
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Set up two-factor authentication
              </V2Typography>
              <V2Typography variant="body" muted>
                To keep your account secure, add a mobile number. We&rsquo;ll text you a
                verification code to finish signing in.
              </V2Typography>
            </div>

            <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-5">
              <V2FormField label="Mobile number" error={error}>
                <V2Input
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 555-1234"
                  prefix={<Phone className="h-4 w-4" aria-hidden />}
                  data-sensitive
                  autoFocus
                />
              </V2FormField>
              <V2FormActions variant="stretch">
                <V2Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || phone.replace(/\D/g, "").length !== 10}
                  data-track="button-company-census-add-phone"
                >
                  {isLoading ? "Sending code…" : "Send verification code"}
                </V2Button>
              </V2FormActions>
            </form>

            <V2Button
              variant="link"
              onClick={() => {
                setAwaitingPhone(false);
                setPhone("");
                setEnrollingPhone(false);
                setError(undefined);
              }}
              data-track="button-company-census-add-phone-back"
            >
              Back to login
            </V2Button>
          </V2CardContent>
        </V2Card>

        <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          <V2Typography variant="meta" muted>
            Encrypted &amp; secure. This is the same login you use for your existing benefits.
          </V2Typography>
        </div>
      </div>
    );
  }

  if (awaitingOtp) {
    return (
      <div key="otp" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                Verify it&rsquo;s you
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Enter your verification code
              </V2Typography>
              <V2Typography variant="body" muted>
                We sent a code to {maskedDestination ?? "your device"}. Enter it below to finish
                signing in.
              </V2Typography>
            </div>

            <form onSubmit={handleOtpSubmit} className="flex flex-col gap-5">
              {/* Attribute-identical to the activation step's OTP input (whose
                  autofill from Messages is verified working); onComplete submits
                  as soon as all six digits land — typed, pasted, or autofilled. */}
              <V2InputOTP
                label="Verification code"
                error={error}
                length={6}
                value={otp}
                onValueChange={setOtp}
                onComplete={(code) => void submitOtpValue(code)}
                autoFocus
              />
              <V2FormActions variant="stretch">
                <V2Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || otp.replace(/\D/g, "").length === 0}
                  data-track="button-company-census-verify"
                >
                  {isLoading ? "Verifying…" : "Verify and continue"}
                </V2Button>
              </V2FormActions>
            </form>

            {otpViaSms && (
              <div className="flex justify-center">
                <OtpResend
                  cooldownSeconds={RESEND_COOLDOWN_SECONDS}
                  onResend={handleResendCode}
                  dataTrack="button-company-census-mfa-resend"
                />
              </div>
            )}

            <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-4">
              {enrollingPhone && (
                <V2Button
                  variant="link"
                  onClick={() => {
                    setAwaitingOtp(false);
                    setOtp("");
                    setError(undefined);
                    setAwaitingPhone(true);
                  }}
                  data-track="button-company-census-mfa-change-phone"
                >
                  Wrong number? Change it
                </V2Button>
              )}
              <V2Button
                variant="link"
                onClick={() => {
                  setAwaitingOtp(false);
                  setOtp("");
                  setError(undefined);
                  setEnrollingPhone(false);
                }}
                data-track="button-company-census-mfa-back"
              >
                Back to login
              </V2Button>
            </div>
          </V2CardContent>
        </V2Card>
      </div>
    );
  }

  return (
    <div key="login" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              Welcome back
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Log in to start your medical quote
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              Use the account you already have with Cakewalk and we&rsquo;ll pull up your business.
              No account yet? You can create one in a minute.
            </V2Typography>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            data-track="form-company-census-login"
          >
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <V2FormField label="Email" error={undefined}>
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}>
              <V2FormField label="Password" error={error}>
                <V2Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  prefix={<Lock className="h-4 w-4" aria-hidden />}
                  suffix={
                    <V2InlineIconButton
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((v) => !v)}
                      icon={
                        showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />
                      }
                    />
                  }
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 2, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || email.trim().length === 0 || password.length === 0}
                data-track="button-company-census-login"
              >
                {isLoading ? "Signing in…" : "Log in"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex justify-center">
            <V2Button
              variant="link"
              onClick={onForgotPassword}
              data-track="button-company-census-forgot-password"
            >
              Forgot your password?
            </V2Button>
          </div>

          <div className="flex justify-center border-t border-cw-v2-border/60 pt-5">
            <V2Button
              variant="link"
              onClick={onGetStarted}
              data-track="button-company-census-get-started"
            >
              First time? Get started
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>

      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. This is the same login you use for your existing benefits.
        </V2Typography>
      </div>
    </div>
  );
}
